import { v4 as uuidv4 } from 'uuid';

import { Row } from '~components/table-view';

import { SahContributionClientGroup } from '~support-at-home/models';

import { ContributionBody } from '~support-at-home/actions/sahClientContributions';

export const CLIENT_ROW_PREFIX = 'client-';
const NEW_ROW_PREFIX = 'new-';

type RowId = string | number;

const toNumberOrNull = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value);

export const isClientRow = (row: Row) => row.row_type === 'client';

// Unsaved rows carry a client-side id that can never collide with a saved (UUID) row.
export const isNewContributionRow = (row: Row) =>
  row.row_type === 'contribution' && String(row.id).startsWith(NEW_ROW_PREFIX);

/**
 * One client row followed by that client's contribution rows, per group. The client row gets a
 * prefixed id so it cannot collide with a contribution UUID (row ids feed selection and DOM ids).
 * A group with no resolvable client keeps its rows and is labelled by id.
 */
export const flattenContributionGroups = (groups: SahContributionClientGroup[] = []): Row[] =>
  groups.flatMap((group) => {
    const { client_id, client, count, contributions = [] } = group;

    const clientRow: Row = {
      id: `${CLIENT_ROW_PREFIX}${client_id}`,
      row_type: 'client',
      client_id,
      client_name: client?.full_name?.trim() || `Unknown client (#${client_id})`,
      has_client: !!client,
      count,
    };

    const contributionRows = contributions.map(
      (item): Row => ({
        id: item.id,
        row_type: 'contribution',
        client_id: item.client_id ?? client_id,
        category_id: item.category_id,
        category: item.category
          ? { ...item.category, max_contrib_proportion: toNumberOrNull(item.category.max_contrib_proportion) }
          : undefined,
        funding_source_id: item.funding_source_id,
        funding_source_name: item.funding_source?.name ?? '',
        contrib_proportion: toNumberOrNull(item.contrib_proportion),
        start_date: item.start_date,
        end_date: item.end_date ?? null,
      }),
    );

    return [clientRow, ...contributionRows];
  });

/** A blank, unsaved contribution row for a client, ready to be edited. */
export const buildNewContributionRow = (clientId: number): Row => ({
  id: `${NEW_ROW_PREFIX}${uuidv4()}`,
  row_type: 'contribution',
  client_id: clientId,
  category_id: '',
  category: undefined,
  funding_source_id: null,
  funding_source_name: '',
  contrib_proportion: null,
  start_date: '',
  end_date: null,
});

/**
 * Inserts `newRow` after the last row belonging to `clientId`. A client that is not in the list
 * (its page has not loaded, say) gets the row appended at the end rather than losing it.
 */
export const insertAfterClient = (rows: Row[], clientId: number, newRow: Row): Row[] => {
  let lastIndex = -1;
  rows.forEach((row, index) => {
    if (row.client_id === clientId) lastIndex = index;
  });

  if (lastIndex === -1) return [...rows, newRow];
  return [...rows.slice(0, lastIndex + 1), newRow, ...rows.slice(lastIndex + 1)];
};

/**
 * Copies fresh server rows into the page's state without losing work in progress.
 *
 * - A saved row being edited keeps its local values (its server copy is discarded).
 * - Unsaved new rows are re-inserted after their client's rows.
 * - Everything else comes from the server, so counts and untouched rows refresh.
 */
export const mergeDrafts = (serverRows: Row[], prevRows: Row[], editingIds: Set<RowId>): Row[] => {
  const prevById = new Map<RowId, Row>(prevRows.map((row) => [row.id, row]));

  const merged = serverRows.map((row) => {
    if (isClientRow(row) || !editingIds.has(row.id)) return row;
    return prevById.get(row.id) ?? row;
  });

  return prevRows
    .filter(isNewContributionRow)
    .reduce((rows, draft) => insertAfterClient(rows, draft.client_id, draft), merged);
};

/** The API body for a row: only the writable fields, and `client_id` only on create. */
export const toContributionBody = (row: Row): ContributionBody => ({
  ...(isNewContributionRow(row) ? { client_id: row.client_id } : {}),
  category_id: row.category_id,
  funding_source_id: row.funding_source_id,
  contrib_proportion: Number(row.contrib_proportion),
  start_date: row.start_date,
  end_date: row.end_date ? row.end_date : null,
});

/** How many clients (as opposed to contribution rows) are in the list. */
export const countLoadedClients = (rows: Row[]): number => rows.filter(isClientRow).length;
